import { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import { deleteRule, getDisableDays } from "../APIs/api-routes.util";
import { DisableRuleRow, ApiMessageResponse } from "../APIs/api.types";
import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { Modal, ModalFooter } from "../components/ui/Modal";
import { EmptyState, LoadingOverlay } from "../components/ui/PagePrimitives";
import { apiSubscribe } from "../lib/api-subscribe";

function DisableDays() {
  const [data, setData] = useState<DisableRuleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pendingDelete, setPendingDelete] = useState<DisableRuleRow | null>(null);
  const fetchCleanup = useRef<(() => void) | null>(null);

  const fetchRules = useCallback(() => {
    fetchCleanup.current?.();
    setLoading(true);
    setError("");
    fetchCleanup.current = apiSubscribe<DisableRuleRow[]>(
      getDisableDays(),
      {
        next: (rows) => {
          setLoading(false);
          const sorted = [...(Array.isArray(rows) ? rows : [])].sort(
            (a, b) => new Date(b.Day).getTime() - new Date(a.Day).getTime()
          );
          setData(sorted);
        },
        error: () => {
          setLoading(false);
          setError("Could not load disabled dates.");
        },
      }
    );
  }, []);

  useEffect(() => {
    fetchRules();
    return () => fetchCleanup.current?.();
  }, [fetchRules]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setLoading(true);
    setError("");
    apiSubscribe<ApiMessageResponse>(
      deleteRule(pendingDelete.ID),
      {
        next: (body) => {
          setLoading(false);
          setPendingDelete(null);
          if (body?.message === "Rule is Deleted") {
            setSuccess("Disabled date removed.");
            fetchRules();
          } else {
            setError("Could not delete this rule.");
          }
        },
        error: () => {
          setLoading(false);
          setPendingDelete(null);
          setError("Could not reach server.");
        },
      }
    );
  };

  return (
    <>
      <LoadingOverlay show={loading} />

      {error && (
        <Alert variant="error" onDismiss={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onDismiss={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <p className="text-2xs text-content-muted">
        Date-specific rules that override regular clinic availability.
      </p>

      {data.length === 0 && !loading ? (
        <EmptyState
          title="No disabled dates"
          description="Use Quick disable on Home or More to close a specific date."
        />
      ) : (
        <ul className="divide-y divide-brand-100 overflow-hidden rounded-xl border border-brand-100 bg-surface shadow-soft">
          {data.map((row, index) => (
            <li
              key={row.ID}
              className={`flex items-center gap-2 px-3 py-2.5 ${
                index % 2 === 0 ? "bg-accent-amber-light/20" : "bg-surface"
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-content-primary">{row.Day}</p>
                <p className="text-2xs text-content-muted">
                  {row.DayStatus}
                  {row.StartTime ? ` · ${row.StartTime}–${row.EndTime}` : ""}
                  {" · Closed: "}
                  <FontAwesomeIcon
                    icon={row.AppointmentClose === "1" || row.AppointmentClose === 1 ? faCheck : faXmark}
                    className={
                      row.AppointmentClose === "1" || row.AppointmentClose === 1
                        ? "text-accent-emerald"
                        : "text-content-muted"
                    }
                  />
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPendingDelete(row)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-accent-rose hover:bg-accent-rose-light"
                aria-label="Delete rule"
              >
                <FontAwesomeIcon icon={faTrash} className="text-xs" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Remove disabled date?"
        description={`Re-enable booking for ${pendingDelete?.Day ?? "this date"}?`}
        footer={
          <ModalFooter>
            <Button variant="secondary" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Remove
            </Button>
          </ModalFooter>
        }
      />
    </>
  );
}

export default DisableDays;
